import { useEffect } from "react";
import { useMutation } from "@apollo/client";
import {
  SET_VOLTAGE_OUTPUT,
  SET_SESSION_LIMIT,
  SET_MANAGEMENT_STRATEGY,
  SET_SOLAR_STRATEGY,
} from "../gql/gql";
import type {
  UserSitesNode,
  SetVoltageOutputArguments,
  SetSessionLimitArguments,
  SetManagementStrategyArguments,
  SetSolarStrategyArguments,
} from "../types";

type SetVoltageOutputResult = { set_voltage_output: { edges: Array<{ node: Partial<UserSitesNode<string>> }> } };
type SetSessionLimitResult = { set_session_limit: { edges: Array<{ node: { salesforce_details?: { id: string; session_limit?: number } } }> } };
type SetManagementStrategyResult = { set_management_strategy: { edges: Array<{ node: Partial<UserSitesNode<string>> }> } };
type SetSolarStrategyResult = { set_solar_strategy: { edges: Array<{ node: Partial<UserSitesNode<string>> }> } };

export function useSetVoltageOutputMutation() {
  const [execute, { loading, error, reset }] = useMutation<SetVoltageOutputResult, SetVoltageOutputArguments>(
    SET_VOLTAGE_OUTPUT,
    { context: { serializationKey: "SET_VOLTAGE_OUTPUT", mutationTimeout: 60000 } }
  );

  const setVoltageOutput = async (vars: SetVoltageOutputArguments, onCompleted?: () => void) => {
    await execute({
      variables: vars,
      onCompleted,
      update: (cache, { data }) => {
        const siteId = data?.set_voltage_output?.edges?.[0]?.node?.id;
        const newFeeds = data?.set_voltage_output?.edges?.[0]?.node?.feeds;
        if (siteId != null && newFeeds != null) {
          cache.modify({
            id: `ChargeSite:${siteId}`,
            fields: { feeds: () => newFeeds },
          });
        }
      },
    });
  };

  useEffect(() => {
    if (loading) {
      const t = setTimeout(reset, 60000);
      return () => clearTimeout(t);
    }
  }, [loading, reset]);

  return [setVoltageOutput, loading, error] as const;
}

export function useSetSessionLimitMutation() {
  const [execute, { loading, error, reset }] = useMutation<SetSessionLimitResult, SetSessionLimitArguments>(
    SET_SESSION_LIMIT,
    { context: { serializationKey: "SET_SESSION_LIMIT", mutationTimeout: 300000 } }
  );

  const setSessionLimit = async (vars: SetSessionLimitArguments, onCompleted?: () => void) => {
    await execute({
      variables: vars,
      onCompleted,
      update: (cache, { data }) => {
        const id = data?.set_session_limit?.edges?.[0]?.node?.salesforce_details?.id;
        const session_limit = data?.set_session_limit?.edges?.[0]?.node?.salesforce_details?.session_limit;
        if (id != null && session_limit != null) {
          cache.modify({
            id: `SalesforceCharger:${id}`,
            fields: { session_limit: () => session_limit },
          });
        }
      },
    });
  };

  useEffect(() => {
    if (loading) {
      const t = setTimeout(reset, 60000);
      return () => clearTimeout(t);
    }
  }, [loading, reset]);

  return [setSessionLimit, loading, error] as const;
}

export function useSetManagementStrategyMutation() {
  const [execute, { loading, error, reset }] = useMutation<SetManagementStrategyResult, SetManagementStrategyArguments>(
    SET_MANAGEMENT_STRATEGY,
    { context: { serializationKey: "SET_MANAGEMENT_STRATEGY", mutationTimeout: 60000 } }
  );

  const setManagementStrategy = async (vars: SetManagementStrategyArguments, onCompleted?: () => void) => {
    await execute({
      variables: vars,
      onCompleted,
      update: (cache, { data }) => {
        const siteId = data?.set_management_strategy?.edges?.[0]?.node?.id;
        const strategy = data?.set_management_strategy?.edges?.[0]?.node?.optimisationStrategy;
        if (siteId != null && strategy != null) {
          cache.modify({
            id: `ChargeSite:${siteId}`,
            fields: { optimisationStrategy: () => strategy },
          });
        }
      },
    });
  };

  useEffect(() => {
    if (loading) {
      const t = setTimeout(reset, 60000);
      return () => clearTimeout(t);
    }
  }, [loading, reset]);

  return [setManagementStrategy, loading, error] as const;
}

export function useSetSolarStrategyMutation() {
  const [execute, { loading, error, reset }] = useMutation<SetSolarStrategyResult, SetSolarStrategyArguments>(
    SET_SOLAR_STRATEGY,
    { context: { serializationKey: "SET_SOLAR_STRATEGY", mutationTimeout: 60000 } }
  );

  const setSolarStrategy = async (vars: SetSolarStrategyArguments, onCompleted?: () => void) => {
    await execute({
      variables: vars,
      onCompleted,
      update: (cache, { data }) => {
        const siteId = data?.set_solar_strategy?.edges?.[0]?.node?.id;
        const strategy = data?.set_solar_strategy?.edges?.[0]?.node?.solarStrategy;
        if (siteId != null && strategy != null) {
          cache.modify({
            id: `ChargeSite:${siteId}`,
            fields: { solarStrategy: () => strategy },
          });
        }
      },
    });
  };

  useEffect(() => {
    if (loading) {
      const t = setTimeout(reset, 60000);
      return () => clearTimeout(t);
    }
  }, [loading, reset]);

  return [setSolarStrategy, loading, error] as const;
}
